// WHAT: Zod schema for validating roadmap item data
// WHY:  Provides type-safe validation for roadmap items with enum constraints
// DOMAIN: Admin → Roadmap → Shared
// DEPENDENCIES: Zod library, roadmap_types for enum types
// RISKS:
//   - NOTE: Schema validation errors may not be user-friendly
//   - NOTE: UUID validation may reject valid UUIDs in some formats
//   - NOTE: Optional fields may cause issues if required in business logic

import { z } from "zod";

/**
 * WHAT:
 *   Zod schema for validating roadmap item data structure with type-safe enums
 *   and required/optional field definitions.
 *
 * WHY:
 *   Ensures data consistency and type safety when creating or updating roadmap items,
 *   preventing invalid data from entering the system.
 *
 * VALIDATION RULES:
 *   - id: Must be valid UUID
 *   - type: Must be one of: bug, feature, insight, idea
 *   - module: Must be non-empty string
 *   - title: Must be non-empty string
 *   - description: Must be non-empty string
 *   - status: Must be one of: open, in_progress, done, rejected
 *   - created_at: Must be valid ISO datetime string
 *   - updated_at: Must be valid ISO datetime string
 *   - priority: Optional, must be one of: low, medium, high
 *   - tags: Optional array of strings
 *   - linked_to: Optional array of UUID strings
 *   - impact_score: Optional, must be one of: low, medium, high
 *   - effort_estimate: Optional, must be one of: low, medium, high
 *
 * RISKS:
 *   - NOTE: UUID validation is strict - may reject valid UUIDs in some formats
 *   - NOTE: Schema doesn't include progress field - may need to be added
 *   - NOTE: Validation errors from Zod may not be user-friendly - consider custom messages
 */
/**
 * Validates ISO 8601 datetime string format
 *
 * @param value - String value to validate
 *
 * @returns {boolean} True if valid ISO datetime string
 *
 */
const isValidISODatetime = (value: string): boolean => {
  if (typeof value !== "string" || value.length === 0) {
    return false;
  }
  const parsed = Date.parse(value);
  return !Number.isNaN(parsed) && parsed > 0;
};

export const Roadmap_Item_Schema = z.object({
  created_at: z.string().refine(isValidISODatetime, {
    message: "Must be a valid ISO 8601 datetime string",
  }),
  description: z.string().min(1),
  effort_estimate: z.enum(["low", "medium", "high"]).optional(),
  id: z.uuid(),
  impact_score: z.enum(["low", "medium", "high"]).optional(),
  linked_to: z.array(z.uuid()).optional(),
  module: z.string().min(1),
  priority: z.enum(["low", "medium", "high"]).optional(),

  status: z.enum(["open", "in_progress", "done", "rejected"]),
  tags: z.array(z.string()).optional(),
  title: z.string().min(1),
  type: z.enum(["bug", "feature", "insight", "idea"]),
  updated_at: z.string().refine(isValidISODatetime, {
    message: "Must be a valid ISO 8601 datetime string",
  }),
});

export type Roadmap_Item_Input = z.input<typeof Roadmap_Item_Schema>;
export type Roadmap_Item_Output = z.output<typeof Roadmap_Item_Schema>;
